import React, { useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import Lottie from 'react-lottie'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import { issueCheckinSalesSplitDetailPdf } from '@/apis/aipass'
import {
  initialPaymentMethods,
  isMultipleChecked,
  PaymentMethodState,
  toPaymentMethodInputs,
  validatePaymentMethods,
} from '@/hooks/use-payment-methods'
import { Button, ButtonType } from '@/components/atoms/button'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useReceiptProviso } from '@/hooks/use-receipt-proviso'
import { initializeSelectBoxStyle } from '@/constants/form'
import { groupBy } from 'lodash'
import { Select, Option } from '@/components/atoms/select'
import { Checkbox } from '@/components/atoms/checkbox'
import { TextInputWithDropdown } from '@/components/molecules/text-input-with-dropdown'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/atoms/modal'

// SalesType と SalesDiscountType を統合して扱うための共通インターフェース
// APIレスポンスには isDiscount など型定義外のフィールドが含まれる場合があるため optional とする
type UsageInput = {
  id: string
  guestRoomAssignId?: string
  guestRoomAssignShortId?: string
  roomNumber?: string
  salesDate: string
  isDiscount?: boolean
  isPaid?: boolean
  quantity?: number
  salesSubjectPrice: number | ''
  salesSubjectName?: string
  salesSubSubjectName?: string
  salesDiscountName?: string
  salesPaymentName?: string
}

type SalesListItem = UsageInput & {
  amountUsed: number
  itemName: string | undefined
}

type Receipt = {
  guestName: string
  provision: string
  sales: SalesListItem[]
  paymentMethods: PaymentMethodState
}

type SplitReceiptPopupProps = {
  onClose: () => void
  checkinId?: string
  reservationId?: string
  customer: { guest: { name: string; company?: string } }
  usageDetails: UsageInput[] | undefined
  discounts: UsageInput[] | undefined
}

type NumberValue = number | 'hidden'

export const SplitReceiptPopup: React.FC<SplitReceiptPopupProps> = ({
  onClose,
  checkinId,
  reservationId,
  customer,
  usageDetails = [],
  discounts = [],
}) => {
  const animationData = require('@/static/images/json/loading.json')
  const customerName = customer?.guest?.name
  const customerCompany = customer?.guest?.company
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { provisoOptions, defaultProvision, cacheProvision: cacheProvision } = useReceiptProviso()
  const [list, setList] = useState<SalesListItem[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [disabled, setDisabled] = useState<boolean>(false)
  const [paymentTouched, setPaymentTouched] = useState(false)
  // 領収書と売上を紐づける
  const [receipts, setReceipts] = useState<Receipt[]>([])
  const [outputLanguage, setOutputLanguage] = useState('ja')
  // 一括選択のための選択中のIDリスト
  const [bulkUpdateTarget, setBulkUpdateTarget] = useState<string[]>([])

  const [showRoomSplitConfirm, setShowRoomSplitConfirm] = useState(false)

  const MAX_RECEIPTS = 100

  // receipts の sales を saleId → receiptIndex の Map で管理し、getNumberValue の線形探索を O(1) にする
  const saleIdToReceiptIndex = useMemo(() => {
    const map = new Map<string, number>()
    receipts.forEach((receipt, index) => {
      receipt.sales.forEach(sale => map.set(sale.id, index))
    })
    return map
  }, [receipts])

  // 部屋番号を持つ明細のユニークな部屋数（上限チェック用）
  const uniqueRoomCount = useMemo(() => new Set(list.filter(item => item.roomNumber).map(item => item.roomNumber)).size, [list])

  const addReceipt = () => {
    if (receipts.length >= MAX_RECEIPTS) return
    setReceipts([
      ...receipts,
      {
        guestName: customerName,
        provision: defaultProvision,
        sales: [],
        paymentMethods: { ...initialPaymentMethods, other: { ...initialPaymentMethods.other, name: t('ReceiptPayment.Other') } },
      },
    ])
  }

  const removeReceipt = (index: number) => {
    receipts.splice(index, 1)
    setReceipts([...receipts])
  }

  const setGuestName = (index: number, value: string) => {
    receipts[index].guestName = value
    setReceipts([...receipts])
  }

  const setProvision = (index: number, value: string) => {
    receipts[index].provision = value
    setReceipts([...receipts])
  }

  const setReceiptPaymentMethodField = <K extends keyof PaymentMethodState, F extends keyof PaymentMethodState[K]>(
    receiptIndex: number,
    key: K,
    field: F,
    value: PaymentMethodState[K][F],
  ) => {
    setReceipts(prev =>
      prev.map((r, i) =>
        i === receiptIndex ? { ...r, paymentMethods: { ...r.paymentMethods, [key]: { ...r.paymentMethods[key], [field]: value } } } : r,
      ),
    )
  }

  const onSaveData = async () => {
    try {
      if (receipts.length === 0) return
      if (receipts.some(r => r.sales.length === 0)) {
        return alert(t('There are receipts that have not been selected for issuance.'))
      }

      setDisabled(true)
      setIsLoading(true)
      // 1枚目の領収書の但し書きを次回のデフォルト値として保存する
      cacheProvision(receipts[0].provision)
      const { url } = await issueCheckinSalesSplitDetailPdf({
        checkinId,
        reservationId,
        isEnOutput: outputLanguage === 'en' ? 1 : 0,
        splitReceipt: receipts.map(receipt => {
          const paymentMethodInputs = toPaymentMethodInputs(receipt.paymentMethods)
          const filterGuestRoomAssignId = Array.from(
            new Set(receipt.sales.flatMap(item => (item.guestRoomAssignId ? [item.guestRoomAssignId] : []))),
          )
          return {
            provision: receipt.provision,
            guestName: receipt.guestName || customerName,
            salesId: receipt.sales.map(item => item.id),
            paymentMethods: paymentMethodInputs.length > 0 ? paymentMethodInputs : undefined,
            filterGuestRoomAssignId: filterGuestRoomAssignId.length > 0 ? filterGuestRoomAssignId : undefined,
          }
        }),
      })
      window.open(url, '_blank')
    } catch (e) {
      errorHandler(e)
    } finally {
      setDisabled(false)
      setIsLoading(false)
    }
  }

  const onChangeNumber = (item: SalesListItem, value: NumberValue) => {
    for (const receipt of receipts) {
      // 選択されている売上を外す
      receipt.sales = receipt.sales.filter(i => i.id !== item.id)
    }

    if (value !== 'hidden') {
      receipts[value].sales.push(item)
    }

    setReceipts([...receipts])
  }

  const isAllSelected = useMemo(() => {
    if (list.length === 0 || bulkUpdateTarget.length === 0) return false
    return list.every(item => bulkUpdateTarget.includes(item.id))
  }, [list, bulkUpdateTarget])

  const onUpdateBulkReceipt = (value: NumberValue) => {
    const removedReceipts = receipts.map(receipt => ({
      ...receipt,
      sales: receipt.sales.filter(item => !bulkUpdateTarget.includes(item.id)),
    }))

    setReceipts(
      removedReceipts.map((receipt, index) => {
        if (value !== 'hidden' && index === value) {
          return {
            ...receipt,
            sales: [...receipt.sales, ...list.filter(item => bulkUpdateTarget.includes(item.id))],
          }
        }
        return receipt
      }),
    )

    setBulkUpdateTarget([])
  }

  // 部屋番号ごとに明細を自動割り当てする。
  // includeHidden が true の場合は現在「非表示」の明細も含めて割り当てる。
  const onSplitByRoom = (includeHidden: boolean) => {
    const targetItems = includeHidden ? list : list.filter(item => getNumberValue(item) !== 'hidden')
    const filtered = targetItems.filter(item => item.roomNumber)
    const grouped = groupBy(filtered, item => item.roomNumber)

    // targetItemsは客室ソート順で並んでいるため、その順序で領収書を作成するために
    // roomNumberのユニークな値を順番に取得する
    const roomOrders = Array.from(new Set(filtered.map(item => item.roomNumber!)))

    if (roomOrders.length === 0) return

    const newReceipts: Receipt[] = roomOrders.map(room => ({
      guestName: customerName,
      provision: defaultProvision || '',
      paymentMethods: {
        ...initialPaymentMethods,
        other: { ...initialPaymentMethods.other, name: t('ReceiptPayment.Other') },
      },
      sales: grouped[room],
    }))

    setReceipts(newReceipts)
    setBulkUpdateTarget([])
  }

  const onClickSplitByRoom = () => {
    // 非表示（現在どの領収書にも割り当てられていない）明細が存在するか確認する
    const hasHiddenItems = list.some(item => getNumberValue(item) === 'hidden' && item.roomNumber)
    if (hasHiddenItems) {
      setShowRoomSplitConfirm(true)
    } else {
      onSplitByRoom(false)
    }
  }

  const onChangeAllCheckbox = () => {
    if (isAllSelected) {
      setBulkUpdateTarget([])
    } else {
      setBulkUpdateTarget(list.map(item => item.id))
    }
  }

  const onChangeCheckbox = (salesId: string) => {
    setBulkUpdateTarget(prev => {
      return prev.includes(salesId) ? prev.filter(id => id !== salesId) : [...prev, salesId]
    })
  }

  const getSumByReceipt = (receipt: Receipt): number => {
    return receipt.sales.reduce((sum, item) => sum + (item?.amountUsed || 0), 0)
  }

  const isPaymentInvalid = receipts.some(
    r => isMultipleChecked(r.paymentMethods) && !!validatePaymentMethods(r.paymentMethods, getSumByReceipt(r)),
  )

  // 売上を何枚目の領収書に含むかを返す。含まれない場合は 'hidden' を返す
  const getNumberValue = (item: SalesListItem): NumberValue => {
    const index = saleIdToReceiptIndex.get(item.id)
    return index !== undefined ? index : 'hidden'
  }

  const disabledSplitByRoomButton = uniqueRoomCount > MAX_RECEIPTS || bulkUpdateTarget.length > 0

  useEffect(() => {
    if (receipts.some(r => r.sales.length)) {
      setDisabled(false)
    } else {
      setDisabled(true)
    }
  }, [receipts])

  useEffect(() => {
    if (!defaultProvision) return
    // 初期化直後はprovisionが空のため、defaultProvisionが確定した時点で全領収書に反映する
    // ユーザーが手入力した値（空でない）は上書きしない
    setReceipts(prev => prev.map(r => ({ ...r, provision: r.provision || defaultProvision })))
  }, [defaultProvision])

  useEffect(() => {
    // SalesDiscountTypeはisDiscount/quantityを型定義上持たないため、discountsを先に正規化して割引扱いを保証する
    const normalizedDiscounts = discounts.map(d => ({ ...d, isDiscount: true, quantity: 1 }))
    const list = Object.values(groupBy([...usageDetails, ...normalizedDiscounts], s => s.guestRoomAssignId))
      .flatMap(g => g)
      .map(usage => {
        // isDiscountがfalseのとき対象はSalesTypeであり必ずquantityを持つ
        const quantity = usage.isDiscount ? 1 : Number(usage.quantity) || 0
        const hasSubSubjectName = usage.salesSubSubjectName && usage.salesSubjectName !== usage.salesSubSubjectName
        const salesSubjectName = hasSubSubjectName ? `${usage.salesSubjectName} ${usage.salesSubSubjectName}` : usage.salesSubjectName
        // salesSubjectPriceは SalesType では number | '' だが演算時は数値として扱う
        const salesSubjectPrice = Number(usage.salesSubjectPrice)
        return {
          ...usage,
          amountUsed: salesSubjectPrice * (usage.isDiscount && salesSubjectPrice ? -1 : 1) * quantity,
          itemName: usage.isDiscount ? usage.salesDiscountName : salesSubjectName || usage.salesPaymentName,
        }
      })
    setList(list)
    setReceipts([
      {
        guestName: customerName,
        provision: '',
        paymentMethods: { ...initialPaymentMethods, other: { ...initialPaymentMethods.other, name: t('ReceiptPayment.Other') } },
        // 未払いかつ割引じゃない、もしくは割引だが金額があるものを初期表示の対象とする
        sales: list.filter(i => !i.isPaid && !(i.isDiscount && i.salesPaymentName && i.salesPaymentName !== '-')),
      },
    ])
  }, [usageDetails, discounts])

  return (
    <div css={modalWrapperStyle}>
      <div css={modalContentStyle}>
        <div css={modalTitleWrapperStyle}>
          <p css={modalTitleStyle}>{t('split issue')}</p>
          <RadioGroupField
            itemCss={radioItemStyle}
            value={outputLanguage}
            style={{ labelMargin: 12 }}
            items={[
              { value: 'ja', label: t('Japanese language') },
              { value: 'en', label: 'English' },
            ]}
            onChange={e => {
              setOutputLanguage(e.target.value)
            }}
          />
        </div>
        <div css={mainContentStyle}>
          <div css={{ padding: 24, width: 263, minWidth: 263, overflowY: 'auto' }}>
            {receipts.map((receipt, index) => {
              return (
                <div
                  key={index}
                  css={{
                    backgroundColor: '#fff',
                    borderRadius: 10,
                    border: '1px solid #ccc',
                    color: '#272727',
                    fontSize: 12,
                    marginTop: index ? 16 : 0,
                  }}
                >
                  <div
                    css={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #ccc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontWeight: 'bold',
                      color: '#676767',
                    }}
                  >
                    <div>
                      {t('Receipt')} | {t('Number')} {index + 1}
                    </div>
                    {index > 0 && (
                      <button
                        css={{ background: 'initial', border: 'none', cursor: 'pointer', margin: '-9px -16px' }}
                        type="button"
                        onClick={() => removeReceipt(index)}
                      >
                        <img src={require('@/static/images/delete_yellow.svg')} />
                      </button>
                    )}
                  </div>

                  <div css={{ padding: '12px 16px' }}>
                    <div css={{ marginBottom: 16 }}>
                      <div css={{ marginBottom: 8, fontSize: 10 }}>{t('Your name')}</div>
                      {customerCompany ? (
                        <TextInputWithDropdown
                          value={receipt.guestName}
                          onChange={value => setGuestName(index, value)}
                          options={[
                            { value: customerName, label: customerName },
                            { value: customerCompany, label: customerCompany },
                          ]}
                          placeholder="山田 太郎"
                          width={173}
                        />
                      ) : (
                        <input
                          name="text"
                          type="text"
                          css={inputStyle}
                          value={receipt.guestName}
                          onChange={e => setGuestName(index, e.target.value)}
                          placeholder="山田 太郎"
                        />
                      )}
                    </div>
                    <div css={{ marginBottom: 16 }}>
                      <div css={{ marginBottom: 8, fontSize: 10 }}>{t('Proviso')}</div>
                      <TextInputWithDropdown
                        value={receipt.provision}
                        onChange={value => setProvision(index, value)}
                        options={provisoOptions}
                        placeholder={t('As accommodation fee')}
                        width={173}
                      />
                    </div>
                    <div>
                      <div css={{ marginBottom: 8, fontSize: 10 }}>{t('ReceiptPayment.PaymentMethod')}</div>
                      <div css={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {/* 現金 */}
                        <Checkbox
                          value={receipt.paymentMethods.cash.checked}
                          label={t('ReceiptPayment.Cash')}
                          onChange={v => setReceiptPaymentMethodField(index, 'cash', 'checked', v)}
                        />
                        {receipt.paymentMethods.cash.checked && isMultipleChecked(receipt.paymentMethods) && (
                          <input
                            type="text"
                            css={splitAmountInputStyle}
                            value={receipt.paymentMethods.cash.amount}
                            onChange={e => setReceiptPaymentMethodField(index, 'cash', 'amount', e.target.value)}
                            onBlur={() => setPaymentTouched(true)}
                            placeholder="1000"
                          />
                        )}
                        {/* クレジットカード */}
                        <Checkbox
                          value={receipt.paymentMethods.creditCard.checked}
                          label={t('ReceiptPayment.CreditCard')}
                          onChange={v => setReceiptPaymentMethodField(index, 'creditCard', 'checked', v)}
                        />
                        {receipt.paymentMethods.creditCard.checked && isMultipleChecked(receipt.paymentMethods) && (
                          <input
                            type="text"
                            css={splitAmountInputStyle}
                            value={receipt.paymentMethods.creditCard.amount}
                            onChange={e => setReceiptPaymentMethodField(index, 'creditCard', 'amount', e.target.value)}
                            onBlur={() => setPaymentTouched(true)}
                            placeholder="1000"
                          />
                        )}
                        {/* その他 */}
                        <div css={{ display: 'flex', alignItems: 'center' }}>
                          <Checkbox
                            value={receipt.paymentMethods.other.checked}
                            onChange={v => setReceiptPaymentMethodField(index, 'other', 'checked', v)}
                          />
                          <input
                            type="text"
                            css={[splitOtherNameInputStyle, !receipt.paymentMethods.other.checked && splitOtherNameDisabledStyle]}
                            value={receipt.paymentMethods.other.name}
                            disabled={!receipt.paymentMethods.other.checked}
                            onChange={e => setReceiptPaymentMethodField(index, 'other', 'name', e.target.value)}
                            onBlur={() => setPaymentTouched(true)}
                            placeholder={t('ReceiptPayment.Other')}
                          />
                        </div>
                        {receipt.paymentMethods.other.checked && isMultipleChecked(receipt.paymentMethods) && (
                          <input
                            type="text"
                            css={splitAmountInputStyle}
                            value={receipt.paymentMethods.other.amount}
                            onChange={e => setReceiptPaymentMethodField(index, 'other', 'amount', e.target.value)}
                            onBlur={() => setPaymentTouched(true)}
                            placeholder="1000"
                          />
                        )}
                        {(() => {
                          const errorKey = paymentTouched ? validatePaymentMethods(receipt.paymentMethods, getSumByReceipt(receipt)) : null
                          return errorKey ? <p css={splitPaymentErrorStyle}>{t(errorKey)}</p> : null
                        })()}
                      </div>
                    </div>
                  </div>
                  <div
                    css={{
                      padding: '12px 16px',
                      borderTop: '1px solid #ccc',
                      display: 'flex',
                      justifyContent: 'space-between',
                      color: '#272727',
                      fontSize: 12,
                    }}
                  >
                    <div>{t('Amount of money')}</div>
                    <div css={{ fontSize: 14, fontWeight: 'bold' }}>{getSumByReceipt(receipt).toLocaleString()}</div>
                  </div>
                </div>
              )
            })}
            {receipts.length < MAX_RECEIPTS && (
              <button
                css={{
                  padding: '12px 16px',
                  background: '#fff',
                  border: '1px dashed #ccc',
                  marginTop: 16,
                  width: '100%',
                  color: '#f2a40b',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
                onClick={addReceipt}
              >
                <img
                  src={require('@/static/images/plus.svg')}
                  css={{ width: 13, height: 13, marginRight: 8, cursor: 'pointer' }}
                  alt={t('Add icon yellow')}
                />
                {t('Add receipt')}
              </button>
            )}
          </div>
          <div css={{ borderLeft: '1px solid #ccc', padding: 24, overflowY: 'auto' }}>
            <div css={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Select
                value={undefined}
                placeholder={t('Bulk change number')}
                onChange={value => onUpdateBulkReceipt(value === 'hidden' ? 'hidden' : Number(value))}
                disabled={bulkUpdateTarget.length === 0}
                customStyle={bulkChangeButtonStyle}
              >
                <Option value={'hidden'}>{t('Hidden')}</Option>
                {receipts.map((receipt, index) => (
                  <Option key={index} value={index}>
                    {t('Receipt sheet number', { number: index + 1 })}
                  </Option>
                ))}
              </Select>
              {bulkUpdateTarget.length > 0 && (
                <span css={{ fontSize: 12, color: '#676767' }}>{t('{{count}} items selected', { count: bulkUpdateTarget.length })}</span>
              )}
              <Button
                width={126}
                height={32}
                buttonType={disabledSplitByRoomButton ? ButtonType.PrimaryDisabled : ButtonType.Primary}
                fontSize={14}
                fontWeight={'normal'}
                onClick={onClickSplitByRoom}
                marginLeft={10}
                isDisabled={disabledSplitByRoomButton}
                title={uniqueRoomCount > MAX_RECEIPTS ? t('Split by room disabled over limit', { count: MAX_RECEIPTS }) : undefined}
              >
                {t('Split by room')}
              </Button>
            </div>
            <div css={tableContainer}>
              <table>
                <thead style={{ verticalAlign: 'middle' }}>
                  <tr>
                    <th>
                      <Checkbox value={isAllSelected} onChange={onChangeAllCheckbox} disabled={list.length === 0} />
                    </th>
                    <th>{t('Number')}</th>
                    <th>
                      {t('Room number')}({t('Room ID')})
                    </th>
                    <th>{t('Date of use')}</th>
                    <th>{t('Reference')}</th>
                    <th>{t('Amount used')}</th>
                    <th>{t('payment')}</th>
                    <th>{t('Deposit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(item => (
                    <tr className={getNumberValue(item) !== 'hidden' ? 'highlight-row' : ''} key={item.id}>
                      <td style={{ width: 0 }}>
                        <Checkbox value={bulkUpdateTarget.includes(item.id)} onChange={() => onChangeCheckbox(item.id)} />
                      </td>
                      <td style={{ width: 0 }}>
                        <select
                          name="checkout_time"
                          css={selectBoxStyle}
                          defaultValue={'hidden'}
                          value={getNumberValue(item)}
                          onChange={e => onChangeNumber(item, e.target.value === 'hidden' ? 'hidden' : Number(e.target.value))}
                        >
                          <option value={'hidden'}>{t('Hidden')}</option>
                          {receipts.map((receipt, index) => (
                            <option key={index} value={index}>
                              {index + 1}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>{item.roomNumber ? `${item.roomNumber}(${item.guestRoomAssignShortId})` : '-'}</td>
                      <td>{dayjs(item.salesDate).format(t('MM-DD'))}</td>
                      <td>{item.itemName}</td>
                      <td>{item.amountUsed?.toLocaleString()}</td>
                      <td>{item.salesPaymentName}</td>
                      <td className={!item.isDiscount && !item.isPaid ? 'orange-text' : ''}>
                        {t(item.isDiscount ? '-' : item.isPaid ? 'Paid' : 'Unpaid')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div>
          <div css={buttomButtonStyle}>
            <Button width={110} height={38} buttonType={3} fontSize={14} marginRight={16} fontWeight={'normal'} onClick={() => onClose()}>
              {t('Cancel')}
            </Button>
            <Button
              width={126}
              fontWeight={'normal'}
              height={38}
              buttonType={disabled || isPaymentInvalid ? ButtonType.PrimaryDisabled : ButtonType.Primary}
              fontSize={14}
              isDisabled={disabled || isPaymentInvalid}
              onClick={() => onSaveData()}
            >
              {t('Download')}
            </Button>
          </div>
        </div>
        {isLoading && (
          <div
            css={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.8)',
            }}
          >
            <Lottie height="80px" width="80px" options={{ animationData: animationData, autoplay: true, loop: true }} />
          </div>
        )}
        {showRoomSplitConfirm && (
          <Modal customCss={{ height: 'auto', minHeight: 'unset' }} containerCss={css({ zIndex: 10001 })}>
            <ModalHeader>{t('Split by room confirm header')}</ModalHeader>
            <ModalBody>
              <p css={{ fontSize: 14, lineHeight: 1.6, color: '#272727' }}>{t('Split by room confirm')}</p>
            </ModalBody>
            <ModalFooter customCss={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 24px' }}>
              <Button
                buttonType={ButtonType.Secondary}
                width={80}
                height={38}
                fontSize={13}
                marginRight={16}
                onClick={() => {
                  setShowRoomSplitConfirm(false)
                  onSplitByRoom(false)
                }}
              >
                {t('No')}
              </Button>
              <Button
                buttonType={ButtonType.Primary}
                width={80}
                height={38}
                fontSize={13}
                onClick={() => {
                  setShowRoomSplitConfirm(false)
                  onSplitByRoom(true)
                }}
              >
                {t('Yes')}
              </Button>
            </ModalFooter>
          </Modal>
        )}
      </div>
    </div>
  )
}

// Modal
const modalWrapperStyle = css({
  width: '100vw',
  height: '100vh',
  background: 'rgba(39, 39, 39, 0.5)',
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: 103,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'scroll',
})
const mainContentStyle = css({
  marginTop: 60,
  height: 'calc(100vh - 240px)',
  background: '#F2F2F2 0% 0% no-repeat padding-box',
  display: 'flex',
  overflowY: 'auto',
})

const inputStyle = css({
  height: 32,
  padding: '8px 16px',
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  textAlign: 'left',
  appearance: 'none',
  width: '100%',
  '::placeholder': { color: '#ccc' },
})

const modalContentStyle = css({
  background: '#fff',
  borderRadius: 5,
  position: 'relative',
  overflow: 'hidden',
})

const modalTitleWrapperStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  paddingInline: 32,
  position: 'absolute',
  'z-index': '10000',
  backgroundColor: 'white',
  borderRadius: '5px 5px 0 0',
})

const modalTitleStyle = css({
  color: '#272727',
  fontSize: 18,
  fontWeight: 'bold',
  lineHeight: '60px',
  letterSpacing: '0.9px',
})

const buttomButtonStyle = css({
  display: 'flex',
  padding: '11px 32px',
  justifyContent: 'flex-end',
  backgroundColor: '#fff',
  borderRadius: '0px 0px 5px 5px',
})

const tableContainer = css({
  width: 874,
  border: '1px solid #ccc',
  borderRadius: 12,
  overflow: 'hidden',
  minHeight: '100%',
  backgroundColor: '#fff',
  table: {
    width: '100%',
    th: {
      color: '#676767',
      padding: 8,
      background: '#f5f5f5',
      borderBottom: '1px solid #ccc',
      fontSize: 12,
      '&:first-of-type': { paddingLeft: 16 },
    },
    td: {
      color: '#272727',
      padding: '10px 8px',
      background: '#f5f5f5',
      borderBottom: '1px solid #F2F2F2',
      fontSize: 14,
      height: 49,
      maxHeight: 49,
      verticalAlign: 'middle',
      '&.orange-text': { color: '#f2a40b' },
      '&:first-of-type': { paddingLeft: 16 },
    },
    'tr.highlight-row': {
      td: { background: '#fff' },
    },
  },
})

const selectBoxStyle = css(initializeSelectBoxStyle, {
  width: 82,
  height: 28,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  backgroundImage: `url(${require('@/static/images/arrow_yellow.svg')})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 10,
  display: 'block',
  paddingLeft: 14,
  paddingRight: 5,
  marginRight: 0,
  backgroundPosition: '90% center',
  fontSize: 12,
})

const radioItemStyle = css({
  paddingLeft: 12,
  fontSize: 12,
  label: { color: '#676767' },
  img: { width: 18, height: 18 },
})

const bulkChangeButtonStyle = css({
  paddingBottom: 0,
  width: 'auto',
  minWidth: 155,
  '.ant-select-selection': {
    backgroundColor: '#F2A40B !important',
    borderColor: '#F2A40B !important',
    borderRadius: '20px !important',
  },
  '.ant-select-selection__placeholder': {
    color: '#fff !important',
  },
  '.ant-select-arrow img': {
    filter: 'brightness(0) invert(1)',
  },
  '.ant-select-disabled': {
    '.ant-select-selection': {
      backgroundColor: '#ccc !important',
      borderColor: '#ccc !important',
    },
    '.ant-select-selection__placeholder': {
      color: '#fff !important',
    },
    '.ant-select-arrow img': {
      filter: 'brightness(0) invert(1)',
    },
  },
})

const splitPaymentErrorStyle = css({
  fontSize: 11,
  color: '#E84828',
  marginTop: 4,
  marginLeft: 26,
})

const splitAmountInputStyle = css({
  height: 32,
  padding: '0 16px',
  border: '1px solid #ccc',
  borderRadius: 16,
  appearance: 'none',
  '::placeholder': { color: '#ccc' },
  marginLeft: 26,
  width: 148,
})

const splitOtherNameInputStyle = css({
  height: 32,
  padding: '0 16px',
  border: '1px solid #ccc',
  borderRadius: 16,
  appearance: 'none',
  width: 148,
  '::placeholder': { color: '#ccc' },
})

const splitOtherNameDisabledStyle = css({
  backgroundColor: '#f2f2f2',
  color: '#ccc',
})
